<template>
  <vs-sidebar
    click-not-close
    position-right
    parent="body"
    default-index="1"
    color="primary"
    class="add-new-data-sidebar items-no-padding"
    spacer
    v-model="isActive"
  >
    <div class="mt-6 flex items-center justify-between px-6">
      <div class="flex items-center">
        <img width="40" :src="ProductIcon" class="mr-4" />
        <div>
          <h4>{{ isNewProduct ? "New " : "" }}Stock product</h4>
          <small v-if="product">{{ product.name }}</small>
        </div>
      </div>
      <feather-icon icon="XIcon" @click.stop="isActive = false" class="cursor-pointer"></feather-icon>
    </div>
    <vs-divider class="mb-0"></vs-divider>

    <component :is="scrollbarTag" class="scroll-area--data-list-add-new" :settings="settings" :key="$vs.rtl">
      <Loading v-if="isLoading" />
      <div v-else>
        <vs-row class="px-6 pt-6">
          <vs-col vs-w="6" class="flex flex-col mb-2">
            <div class="mb-1 text-sm">
              <span>Number</span>
            </div>
            <div>
              <div v-if="!isNewProduct">
                <code>{{ product.number }}</code>
              </div>
              <div v-else>
                <vs-input class="w-full pr-4" v-model="product.number" />
              </div>
            </div>
          </vs-col>

          <vs-col vs-w="6" class="flex flex-col mb-2" v-if="isNewProduct">
            <div class="mb-1 text-sm flex">
              <span>Unlock date</span>
              <vx-tooltip
                text="Enable unlock the stock product at a given date. If no date is given, the stock product is locked"
                position="bottom"
              >
                <feather-icon icon="InfoIcon" svg-classes="h-4 w-4" class="ml-1" />
              </vx-tooltip>
            </div>
            <div class="flex items-center">
              <div class="flex-grow overflow-hidden">
                <v-date-picker
                  v-model="product.unlockDate"
                  mode="dateTime"
                  class="max-w-full"
                  is24hr
                  :attributes="[{ highlight: { fillMode: 'light' }, dates: new Date() }]"
                >
                  <template v-slot="{ inputValue, inputEvents }">
                    <vs-input :value="inputValue" v-on="inputEvents" class="max-w-full mr-0" :disabled="!enableUnlockDateField" />
                  </template>
                </v-date-picker>
              </div>
              <div>
                <vs-switch v-model="enableUnlockDateField" class="ml-2" />
              </div>
            </div>
          </vs-col>

          <vs-col vs-w="6" class="flex flex-col mb-2" v-else>
            <div class="mb-1 text-sm">
              <span>Status</span>
            </div>
            <div>
              <code v-if="product.locked && hasUnlockDate">
                Locked
                <vx-tooltip
                  :text="`Will automatically unlock at ${formatDate(
                    product.lockAction.when
                  )}. You can manually unlock it at any given time prior to this`"
                  position="bottom"
                  class="inline-block"
                >
                  <feather-icon icon="ClockIcon" svg-classes="h-4 w-4" />
                </vx-tooltip>
              </code>
              <code v-else-if="product.locked">Locked</code>
              <!-- TODO If has unlock date-->
              <code v-else>Unlocked</code>
              <vx-tooltip :text="`Click to ${product.locked ? 'unlock' : 'lock'}`" position="bottom" class="inline-block">
                <feather-icon
                  @mouseover="hoveringLockField = true"
                  @mouseleave="hoveringLockField = false"
                  class="cursor-pointer ml-2"
                  :icon="product.locked ? (hoveringLockField ? 'UnlockIcon' : 'LockIcon') : hoveringLockField ? 'LockIcon' : 'UnlockIcon'"
                  svgClasses="w-4 h-4"
                  @click="changeLockStatus"
                />
              </vx-tooltip>
            </div>
          </vs-col>
        </vs-row>

        <vs-divider />

        <div class="px-6">
          <div class="flex flex-col mb-3">
            <div class="mb-1 text-sm">
              <span>Name</span>
            </div>
            <div>
              <vs-input class="w-full" v-model="product.name" />
            </div>
          </div>

          <div class="flex flex-col mb-3">
            <div class="mb-1 text-sm">
              <span>Country</span>
            </div>
            <div>
              <vs-select v-model="product.countryId" class="w-4/5" autocomplete>
                <vs-select-item :key="index" :value="item.countryId.id" :text="item.name" v-for="(item, index) in countries" />
              </vs-select>
            </div>
          </div>

          <div class="flex flex-col mb-3">
            <div class="mb-1 text-sm">
              <span>Category</span>
            </div>
            <div class="flex">
              <vs-select v-model="product.productCategoryId" class="w-4/5" autocomplete>
                <vs-select-item
                  :key="index"
                  :value="item.productCategoryId.id"
                  :text="item.name"
                  v-for="(item, index) in productCategories"
                />
              </vs-select>
            </div>
          </div>

          <div class="flex flex-col mb-3">
            <div class="mb-1 text-sm">
              <span>VAT rate</span>
            </div>
            <div class="flex">
              <vs-select v-model="product.taxGroupId" class="w-4/5" autocomplete>
                <vs-select-item :key="index" :value="item.id" :text="item.code" v-for="(item, index) in taxGroups" />
              </vs-select>
            </div>
          </div>

          <div class="flex flex-col mb-3">
            <div class="mb-1 text-sm">
              <span>Vendor</span>
            </div>
            <div class="flex">
              <vs-select v-model="product.vendorId" class="w-4/5" autocomplete>
                <vs-select-item :key="index" :value="item.vendorId.id" :text="item.name" v-for="(item, index) in vendors" />
              </vs-select>
            </div>
          </div>

          <div class="flex flex-col mb-3">
            <div class="mb-1 text-sm">
              <span>Alternative vendors</span>
            </div>
            <div class="flex">
              <vs-select v-model="product.alternativeVendorIds" class="w-4/5" autocomplete multiple>
                <vs-select-item :key="index" :value="item.vendorId.id" :text="item.name" v-for="(item, index) in vendors" />
              </vs-select>
            </div>
          </div>

          <div class="flex flex-col mb-3">
            <div class="mb-1 text-sm">
              <span>Tags</span>
            </div>
            <div class="flex">
              <vs-chips placeholder="New tag" class="w-full py-0" v-model="product.tags">
                <vs-chip @click="removeTag(tag)" :key="tag" v-for="tag in product.tags" class="my-1 ml-2 mr-0" closable>
                  {{ tag }}
                </vs-chip>
              </vs-chips>
            </div>
          </div>
        </div>

        <vs-divider class="mt-6" />

        <vs-row class="px-6">
          <vs-col vs-w="6" class="flex flex-col mb-2">
            <div class="text-sm">
              <span>Unit price</span>
            </div>
            <div>
              <div class="flex">
                <vs-input-number v-model="product.unitPrice" :step="0.05" />
              </div>
            </div>
          </vs-col>

          <vs-col vs-w="6" class="flex flex-col mb-2">
            <div class="text-sm">
              <span>Unit amount</span>
            </div>
            <div>
              <div class="flex items-center">
                <div class="flex inline-block">
                  <vs-input-number :min="0" :step="1" v-model="product.itemQuantity" />
                </div>
                <div class="ml-2">
                  <vs-select width="74px" v-model="product.itemMeasurementUnit">
                    <vs-select-item :key="unit.id" :value="unit.id" :text="unit.name" v-for="unit in units" />
                  </vs-select>
                </div>
              </div>
            </div>
          </vs-col>

          <vs-col vs-w="6" class="flex flex-col mb-2">
            <div class="text-sm">
              <span>Case size</span>
            </div>
            <div>
              <div class="flex">
                <vs-input-number v-model="product.caseSize" :step="1" />
              </div>
            </div>
          </vs-col>
        </vs-row>

        <vs-divider class="mt-2" />

        <vs-row class="px-6">
          <vs-col vs-w="12" class="flex flex-col mb-2">
            <div class="text-sm">
              <span>Maximum allowed deviation</span>
            </div>
            <div>
              <div class="flex">
                <vs-input-number v-model="product.allowedDeviationPercentage" :step="0.5" :min="0" :max="100" label="Percentage" />
              </div>
            </div>
          </vs-col>
        </vs-row>
        <stock-product-history v-if="!isNewProduct" :stockProductId="product.id" class="px-6" />

        <div v-if="isNewProduct">
          <vs-divider class="mt-2" />

          <h5 class="pl-6 mb-4">Assign as stock</h5>

          <vs-row class="px-6">
            <vs-col vs-w="12" class="flex flex-col mb-2">
              <div class="mb-1 text-sm">
                <span>Mandatory stock</span> <br />
                <small>Assign this product as mandatory to the following venues</small>
              </div>
              <venue-selector
                v-model="product.assignAsMandatoryToVenueIds"
                :multiple="true"
                placeholder="Select venues"
                :filterCountries="[activeCountry.name]"
              />
            </vs-col>
          </vs-row>
          <vs-row class="px-6">
            <vs-col vs-w="12" class="flex flex-col mb-2">
              <div class="mb-1 text-sm">
                <span>Optional stock</span> <br />
                <small>Assign this product as optional to the following venues</small>
              </div>
              <venue-selector
                v-model="product.assignAsOptionalToVenueIds"
                :multiple="true"
                placeholder="Select venues"
                :filterCountries="[activeCountry.name]"
              />
            </vs-col>
          </vs-row>
          <div style="height: 200px"></div>
        </div>
      </div>
    </component>

    <div class="flex flex-wrap items-center p-6" slot="footer">
      <vs-button class="mr-6" @click="submitData" :disabled="!isFormValid">{{ isNewProduct ? "Create" : "Save" }}</vs-button>
      <vs-button type="border" color="danger" @click="isActive = false">Cancel</vs-button>
    </div>
  </vs-sidebar>
</template>

<script>
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import ProductIcon from "@/assets/images/product-icon.png";
import StockProductHistory from "./requests/StockProductHistory.vue";
import moment from "moment";
import { mapGetters } from "vuex";
import VenueSelector from "@/components/VenueSelector.vue";

export default {
  components: {
    VenueSelector,
    VuePerfectScrollbar,
    StockProductHistory
  },
  data() {
    return {
      settings: {
        // perfectscrollbar settings
        maxScrollbarLength: 60,
        wheelSpeed: 0.6
      },
      ProductIcon,

      isActive: false,
      isLoading: true,

      isNewProduct: false,
      product: null,

      enableUnlockDateField: false,
      hoveringLockField: false
    };
  },
  computed: {
    ...mapGetters({
      taxGroups: "taxGroups/taxGroups",
      vendors: "vendors/vendors",
      countries: "countries/countries",
      productCategories: "productCategories/productCategories",
      activeCountry: "venues/activeCountry",
      units: "units/units"
    }),
    hasUnlockDate() {
      console.log("LOCKED DATE TIME", moment(this.product.lockAction.when).year());
      return (
        this.product.lockAction && this.product.lockAction.when && moment(this.product.lockAction.when).year() > 2000 // Over year 200 (aka over DateTime.MinValue in C#)
      );
    },
    isFormValid() {
      if (!this.product) return false;
      if (!this.product.name) return false;
      if (!this.product.countryId) return false;
      if (!this.product.productCategoryId) return false;
      if (!this.product.vendorId) return false;

      if (this.isNewProduct) {
        if (!this.product.number) return false;
      }

      return true;
    },
    scrollbarTag() {
      return this.$store.getters.scrollbarTag;
    }
  },
  watch: {
    // Watch the product.assignAsOptionalToVenueIds and product.assignAsMandatoryToVenueIds
    // and remove the venues from the other list if they are added to the other list
    "product.assignAsOptionalToVenueIds": function (newVal, oldVal) {
      // Remove from product.assignAsMandatoryToVenueIds
      if (JSON.stringify(newVal) === JSON.stringify(oldVal)) return;
      this.product.assignAsMandatoryToVenueIds = this.product.assignAsMandatoryToVenueIds.filter(e => !newVal.includes(e));
    },
    "product.assignAsMandatoryToVenueIds": function (newVal, oldVal) {
      // Remove from product.assignAsOptionalToVenueIds
      if (JSON.stringify(newVal) === JSON.stringify(oldVal)) return;
      this.product.assignAsOptionalToVenueIds = this.product.assignAsOptionalToVenueIds.filter(e => !newVal.includes(e));
    }
  },
  methods: {
    async show(stockProductId) {
      this.isNewProduct = false;

      this.isActive = true;
      this.isLoading = true;
      var result = await this.$store.dispatch("stockProducts/getStockProduct", stockProductId);

      this.product = {
        id: result.id,
        number: result.number,
        name: result.name,
        vendorId: result.vendor ? result.vendor.vendorId.id : null,
        countryId: result.country ? result.country.countryId.id : null,
        alternativeVendorIds: result.alternativeVendors.map(e => e.vendorId.id),
        productCategoryId: result.productCategory ? result.productCategory.productCategoryId.id : null,
        taxGroupId: result.taxGroupId ? result.taxGroupId.id : null,
        caseSize: result.caseSize,
        unitPrice: result.unitPrice,
        // inactive: result.inactive,
        locked: result.locked,
        lockAction: result.lockAction,
        allowedDeviationPercentage: result.allowedDeviationPercentage,

        tags: result.tags ? result.tags : [],
        itemQuantity: result.itemQuantity ? result.itemQuantity.amount : 0,
        itemMeasurementUnit: result.itemQuantity ? result.itemQuantity.unit : "cl"
      };

      await this.loadExternalData();
      console.log("Product", this.product);
      this.isLoading = false;
    },
    async showEmptyProduct() {
      this.isNewProduct = true;
      this.product = {
        id: "",
        number: "",
        name: "",
        vendorId: null,
        countryId: null,
        taxGroupId: null,
        alternativeVendorIds: [],
        productCategoryId: null,
        caseSize: 1,
        unitPrice: 10,
        // inactive: true,
        locked: true,
        unlockDate: null,
        allowedDeviationPercentage: 0,
        assignAsOptionalToVenueIds: [],
        assignAsMandatoryToVenueIds: [],

        tags: [],
        itemQuantity: 50,
        itemMeasurementUnit: "cl"
      };

      this.isActive = true;
      await this.loadExternalData();
      this.isLoading = false;
    },

    async loadExternalData() {
      await Promise.all([
        // this.$store.dispatch("countries/getCountries"),
        this.$store.dispatch("vendors/getVendors"),
        this.$store.dispatch("productCategories/getProductCategories"),
        this.$store.dispatch("taxGroups/getTaxGroups")
      ]);
    },

    formatDate(date) {
      return moment(date).format("DD-MM-YYYY HH:mm");
    },

    async changeLockStatus() {
      console.log("changeLockStatus", this.product.locked);
      try {
        if (this.product.locked) {
          this.$store.dispatch("stockProducts/unlock", this.product.id);
        } else {
          this.$store.dispatch("stockProducts/lock", this.product.id);
        }

        this.$vs.notify({
          title: "Success",
          text: `Successfully ${this.product.locked ? "unlocked" : "locked"} product`
        });
        this.product.locked = !this.product.locked;
      } catch {
        this.$vs.notify({
          title: "Error",
          text: `Failed to ${this.product.locked ? "unlock" : "lock"} product`
        });
      }
    },

    async submitData() {
      console.log("Submitting DTO", this.product);
      let newCreatedProductResponse = null;

      // try {
      if (this.isNewProduct) {
        newCreatedProductResponse = await this.$store.dispatch("stockProducts/createStockProduct", {
          stockProductDto: this.product
        });
        console.log("New created product", newCreatedProductResponse);
      } else {
        await this.$store.dispatch("stockProducts/updateStockProduct", {
          stockProductId: this.product.id,
          stockProductDto: this.product
        });
      }

      this.product = null;
      this.isActive = false;
      this.isLoading = true;

      console.log("New created product", newCreatedProductResponse);

      // If we just created a new product. Show the sidebar again with the product
      if (newCreatedProductResponse) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // FIXME - Wait for backend to update
        this.show(newCreatedProductResponse.id);
      }

      this.$vs.notify({
        title: "Success",
        text: `Successfully ${this.isNewProduct ? "created new" : "updated"} stock product`
      });
      // } catch (error) {
      //   this.$vs.notify({
      //     title: "Error",
      //     color: "danger",
      //     text: `Failed to ${
      //       this.isNewProduct ? "create" : "update"
      //     } stock product: ${error}`,
      //   });

      //   this.product = null;
      //   this.isActive = false;
      //   this.isLoading = true;
      // }
    }
  }
};
</script>

<style lang="scss" scoped>
.add-new-data-sidebar {
  ::v-deep .vs-sidebar--background {
    z-index: 52010;
  }

  ::v-deep .vs-sidebar {
    z-index: 52010;
    width: 400px;
    max-width: 90vw;

    .img-upload {
      margin-top: 2rem;

      .con-img-upload {
        padding: 0;
      }

      .con-input-upload {
        width: 100%;
        margin: 0;
      }
    }
  }
}

.scroll-area--data-list-add-new {
  // height: calc(var(--vh, 1vh) * 100 - 4.3rem);
  height: calc(var(--vh, 1vh) * 100 - 16px - 45px - 82px);

  &:not(.ps) {
    overflow-y: auto;
  }
}
</style>